import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ICONS } from "src/app/_helpers/icons.constants";
import { ModalComponent } from "src/app/shared/design-system/modal/modal.component";
import { ModalConfig } from "src/app/shared/design-system/modal/modal.modal";
import { ButtonComponent } from "src/app/shared/design-system/button/button.component";
import { ButtonConfig } from "src/app/shared/design-system/button/button.modal";
import { SmartImportPageGroup } from "src/app/_models/credit-card/smart-import";
import { SmartImportPageGroupActionEnum } from "src/app/_models/enum/solo-enum";

@Component({
  selector: "smart-import-accept-suggestions",
  templateUrl: "./smart-import-accept-suggestions.component.html",
  standalone: true,
  imports: [CommonModule, TranslateModule, ModalComponent, ButtonComponent],
})
export class SmartImportAcceptSuggestionsComponent {
  // Every group here is still unclassified — the pill previews what confirming will
  // apply (the AI's own recommendation), not a decision that's already been made.
  @Input() groups: SmartImportPageGroup[] = [];
  @Input() remainingCount = 0;
  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  public readonly ICONS = ICONS;
  private readonly Action = SmartImportPageGroupActionEnum;

  // Visibility is actually driven by the wizard mounting/unmounting this whole component
  // (@if (showAcceptSuggestionsModal)), so this never needs to flip back to false itself —
  // it just has to start true so ds-modal renders as soon as this component exists.
  modalConfig: ModalConfig;
  cancelButtonConfig: ButtonConfig;
  confirmButtonConfig: ButtonConfig;

  constructor(private translate: TranslateService) {
    this.modalConfig = {
      id: "smartImportConfirmSuggestions",
      visible: true,
      size: "md",
      title: this.translate.instant("smart_import_confirm_ai_suggestions"),
      ariaLabelledBy: "smartImportConfirmSuggestionsTitle",
    };
    this.cancelButtonConfig = {
      id: "smartImportConfirmSuggestionsCancel",
      label: this.translate.instant("cancel"),
      color: "primary-outline",
      size: "md",
      block: true
    };
    this.confirmButtonConfig = {
      id: "smartImportConfirmSuggestionsConfirm",
      label: this.translate.instant("smart_import_confirm"),
      color: "smart-ai",
      size: "md",
      block: true
    };
  }

  // The groups here are still unclassified — the pill previews what confirming will
  // apply (the AI's own recommendation), not an actual decision yet.
  statusClassFor(group: SmartImportPageGroup): string {
    if (group.aiRecommendation === this.Action.Import) return "--success";
    if (group.aiRecommendation === this.Action.Skip) return "--secondary";
    if (group.aiRecommendation === this.Action.NeverImport) return "--incomplete";
    return "--pending";
  }

  statusLabelKeyFor(group: SmartImportPageGroup): string {
    if (group.aiRecommendation === this.Action.Import) return "label_import";
    if (group.aiRecommendation === this.Action.Skip) return "skip";
    if (group.aiRecommendation === this.Action.NeverImport) return "smart_import_never_import";
    return "smart_import_pending_review";
  }

  // ds-modal's own close (X icon / backdrop click) emits visibleChange(false) — treat
  // that the same as clicking our own Cancel button.
  onModalVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cancelled.emit();
    }
  }
}
